 
import { Application } from "../model_gen/application";
import {
  SandboxApplication,
  SandboxMessage,
  SandboxSession,
} from "../model_gen/sandbox";
import GeneralService from "./GeneralService";
import { Component } from "react";
import { Observable } from "rxjs";

export class SandboxService extends Component {
  public static newApplication(
    app: SandboxApplication,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.post(
      "/sandbox/application",
      JSON.stringify(app),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      (apiKey = apiKey)
    );
  }

  public static updateApplication(
    app: Partial<SandboxApplication>,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.put(
      "/sandbox/application",
      JSON.stringify(app),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getApplications(loadingHandler: any, apiKey: string) {
    return GeneralService.get(
      "/sandbox/application",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getApplication(
    applicationId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static newSession(
    session: Partial<SandboxSession>,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.post(
      "/sandbox/session",
      JSON.stringify(session),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getSessions(
    applicationId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}/session`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getSession(
    applicationId: number,
    sessionId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}/session/${encodeURIComponent(
        sessionId
      )}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static deleteSession(
    sessionId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.delete(
      `/sandbox/session/${encodeURIComponent(sessionId)}`,
      "",
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static newMessage(
    message: Partial<SandboxMessage>,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.post(
      "/sandbox/message",
      JSON.stringify(message),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      (apiKey = apiKey)
    );
  }

  public static getMessages(
    applicationId: number,
    sessionId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}/session/${encodeURIComponent(
        sessionId
      )}/message`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getMessage(
    applicationId: number,
    sessionId: number,
    messageId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}/session/${encodeURIComponent(
        sessionId
      )}/message/${encodeURIComponent(messageId)}`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static mapApplication(
    applicationId: number,
    application: Application,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.post(
      `/sandbox/application/${applicationId}/map`,
      JSON.stringify(application),
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static getMappedApplication(
    applicationId: number,
    loadingHandler: any,
    apiKey: string
  ) {
    return GeneralService.get(
      `/sandbox/application/${applicationId}/map`,
      loadingHandler,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }

  public static newSessionStream(
    session: SandboxSession,
    apiKey: string,
    applicationName?: string
  ): Observable<any> {
    return new Observable((observer) => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 60000);

      const fetchData = async () => {
        try {
          const baseUrl = GeneralService.PROD_ENDPOINT;
          const url = `${baseUrl}/sandbox/session/sync/stream_freeform`;
          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": apiKey,
              Connection: "keep-alive",
              Accept: "application/x-ndjson",
            },
            body: JSON.stringify({
              ...session,
              meta: {
                ...session.meta,
                ...(applicationName && { application_name: applicationName }),
              },
            }),
          };

          const response = await fetch(url, {
            ...options,
            signal: abortController.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error("Response body is not readable");
          }

          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (buffer.trim()) {
                try {
                  observer.next(JSON.parse(buffer));
                } catch (e) {
                  console.error("Error parsing final JSON from buffer:", e);
                }
              }
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (line.trim() === "") continue;

              try {
                const data = JSON.parse(line);
                observer.next(data);
              } catch (e) {
                console.error("Error parsing JSON from stream:", line, e);
              }
            }
          }

          observer.complete();
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.error("Stream request timed out after 60 seconds");
          } else {
            console.error("Error in stream fetch:", error);
          }
          observer.error(error);
        }
      };

      fetchData();

      return () => {
        console.log("Stream subscription closed");
        abortController.abort();
        clearTimeout(timeoutId);
      };
    });
  }

  public static newMessageStream(
    message: SandboxMessage,
    messages: SandboxMessage[],
    apiKey: string
  ): Observable<any> {
    return new Observable((observer) => {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 60000);

      const fetchData = async () => {
        try {
          const baseUrl = GeneralService.PROD_ENDPOINT;
          const url = `${baseUrl}/sandbox/message/sync/freeform`;

          const payload = {
            message: message,
            messages: messages,
          };

          try {
            JSON.stringify(payload);
          } catch (e) {
            console.error("[SandboxService] Error in payload format:", e);
            payload.messages = [];
          }

          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": apiKey,
              Connection: "keep-alive",
              Accept: "application/x-ndjson",
            },
            body: JSON.stringify(payload),
          };

          const response = await fetch(url, {
            ...options,
            signal: abortController.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error("Response body is not readable");
          }

          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (buffer.trim()) {
                try {
                  observer.next(JSON.parse(buffer));
                } catch (e) {
                  console.error("Error parsing final JSON from buffer:", e);
                }
              }
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (line.trim() === "") continue;

              try {
                const data = JSON.parse(line);
                observer.next(data);
              } catch (e) {
                console.error("Error parsing JSON from stream:", line, e);
              }
            }
          }

          observer.complete();
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.error("Stream request timed out after 60 seconds");
          } else {
            console.error("Error in stream fetch:", error);
          }
          observer.error(error);
        }
      };

      fetchData();

      return () => {
        console.log("Stream subscription closed");
        abortController.abort();
        clearTimeout(timeoutId);
      };
    });
  }

  public static branchSession(
    companyId: number,
    agentId: number,
    parentSessionId: string,
    turnNumber: number,
    sandboxApplicationId: number | null,
    apiKey: string
  ) {
    const payload = {
      company_id: companyId,
      agent_id: agentId,
      parent_session_id: parentSessionId,
      turn_number: turnNumber,
      sandbox_application_id: sandboxApplicationId,
    };
    return GeneralService.post(
      "/sandbox/session/branch",
      JSON.stringify(payload),
      null,
      JSON,
      GeneralService.PROD_ENDPOINT,
      apiKey
    );
  }
}
